import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ChatSupportWidget } from "@/components/ChatSupportWidget";
import { Seo } from "@/components/Seo";

/**
 * Shared chrome for legal / info pages (Help, FAQ, Contact, Privacy).
 * Minimal, readable, on-brand.
 */
export function StaticPageShell({ eyebrow, title, lead, children, seo }) {
  return (
    <div className="relative min-h-screen bg-background text-foreground grain">
      {seo && <Seo {...seo} />}
      <div className="absolute inset-0 -z-10 grid-overlay opacity-20" />
      <div className="absolute left-1/2 top-0 -z-10 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-primary/10 blur-[140px]" />

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="back-home-logo">
            <Logo />
          </Link>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Link to="/help" className="hidden hover:text-foreground sm:inline" data-testid="nav-help">Help</Link>
            <Link to="/faq" className="hidden hover:text-foreground sm:inline" data-testid="nav-faq">FAQ</Link>
            <Link to="/contact" className="hidden hover:text-foreground sm:inline" data-testid="nav-contact">Contact</Link>
            <Link to="/privacy" className="hidden hover:text-foreground sm:inline" data-testid="nav-privacy">Privacy</Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16">
        <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-home-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Sabzy
        </Link>
        {eyebrow && <div className="label-mono text-primary">[ {eyebrow} ]</div>}
        <h1
          className="mt-2 text-4xl font-light tracking-[-0.03em] sm:text-5xl"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          {title}
        </h1>
        {lead && (
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">{lead}</p>
        )}
        <div className="prose prose-invert mt-10 max-w-none text-[15px] leading-relaxed text-foreground/85">
          {children}
        </div>

        <footer className="mt-20 border-t border-border/60 pt-8 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>© 2026 Sabzy AI · Premium intelligence terminal</span>
            <div className="flex gap-4">
              <Link to="/help" className="hover:text-foreground">Help</Link>
              <Link to="/faq" className="hover:text-foreground">FAQ</Link>
              <Link to="/contact" className="hover:text-foreground">Contact</Link>
              <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            </div>
          </div>
        </footer>
      </main>

      <ChatSupportWidget />
    </div>
  );
}
